import { useQueries, useQuery } from "@tanstack/react-query";

import { useApiClient } from "@/api/client-context";
import type { CaseListFilters } from "@/api/endpoints/build-case-report-metadata";
import { allCasesQueryOptions } from "@/features/all-cases/use-all-cases";
import { caseHistoryQueryOptions } from "@/features/case-detail/use-case-detail";

import {
  type ActivityEntry,
  type ActivityFilters,
  buildActivityEntries,
} from "./build-activity-entries";

/**
 * 履歴を取りに行く案件数の上限。
 *
 * 横断でイベントを取る API が無いため案件ごとに 1 リクエストになる。
 * 上限を切らないと案件数に比例して際限なく叩くので、直近この件数に限る。
 */
export const ACTIVITY_CASE_LIMIT = 25;

/** 案件側は絞り込まない（絞り込みは束ねたイベントに対して行う）。 */
const NO_CASE_FILTERS: CaseListFilters = {
  keyword: "",
  statuses: [],
  processModelId: null,
  submittedFrom: null,
  submittedTo: null,
};

export type ActivityLogResult = {
  readonly entries: readonly ActivityEntry[];
  readonly isPending: boolean;
  readonly isError: boolean;
  /** 履歴を取れなかった案件数（権限や通信エラー）。0 でなければ画面に出す。 */
  readonly failedCaseCount: number;
};

/**
 * 横断の操作履歴。
 *
 * 直近の案件を一覧で取り、それぞれの履歴（process_instance_event）をまとめて
 * 引いて時系列に束ねる。監査ログ（改ざん耐性・エージェントの帰属・業務データの
 * 前後状態）ではない。詳細は README の「操作履歴」を参照。
 */
export function useActivityLog(filters: ActivityFilters): ActivityLogResult {
  const client = useApiClient();

  const cases = useQuery(
    allCasesQueryOptions(client, {
      filters: NO_CASE_FILTERS,
      page: 1,
      perPage: ACTIVITY_CASE_LIMIT,
    }),
  );

  const histories = useQueries({
    queries: (cases.data?.items ?? []).map((item) =>
      caseHistoryQueryOptions(client, {
        caseId: item.caseId,
        processModelId: item.processModelId,
      }),
    ),
  });

  const items = cases.data?.items ?? [];
  const source = items.map((item, index) => ({
    case: item,
    events: histories[index]?.data ?? [],
  }));

  return {
    entries: buildActivityEntries(source, filters),
    isPending: cases.isPending || histories.some((query) => query.isPending),
    isError: cases.isError,
    failedCaseCount: histories.filter((query) => query.isError).length,
  };
}
